import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useFragment } from "react-relay"
import { UserAvatarTooltip } from "@/components/avatar-user-tooltip"
import { Card, CardBody, CardHeader } from "@/components/ui/card"
import { UserProvider } from "@/components/user-wrapper"
import { formatDateString } from "@/lib/format-date"
import { getEpisodeOfCareFragment } from "@/modules/patients/detail/episode-of-care/lib/get-episode-of-care-fragment"
import { EpisodeOfCareFragment$key } from "@/modules/patients/detail/episode-of-care/types/episode-of-care-fragment-type"
import { DocumentList } from "@/modules/patients/detail/progress-note/documents/document-list"
import { ProgressNoteAndParentData } from "@/modules/patients/detail/progress-note/progress-note-card"
import { ProgressNoteDocument } from "@/modules/patients/detail/progress-note/types/document"
import { useDocumentStore } from "@/modules/patients/detail/store/useDocumentStore"
import { useCountryParam } from "@/modules/patients/hooks/useCountryParam"

interface ProgressNoteCardProps {
  progressNoteData: ProgressNoteAndParentData
  onSelect: (data: ProgressNoteDocument) => void
}

const ProgressNoteCard = ({ progressNoteData, onSelect }: ProgressNoteCardProps) => {
  const { episodeOfCareProgressNoteData: progressNote, parentEpisodeOfCare } = progressNoteData
  const { t } = useTranslation()
  const country = useCountryParam()
  const episodeOfCare = useFragment<EpisodeOfCareFragment$key>(getEpisodeOfCareFragment(country), parentEpisodeOfCare)
  const chFormattedProgressNotes = useDocumentStore((state) => state.chFormattedProgressNotes)
  const deFormattedProgressNotes = useDocumentStore((state) => state.deFormattedProgressNotes)
  const {
    id,
    appointment: { service },
    updatedBy,
    createdAt,
    treatmentPlan,
  } = progressNote

  const hasDocuments = useMemo(() => {
    const progressNoteDataByCountry = (() => {
      switch (country) {
        case "CH":
          return { ...chFormattedProgressNotes?.get(id) }
        case "DE":
          return { ...deFormattedProgressNotes?.get(id) }
      }
    })()

    return Object.values(progressNoteDataByCountry).some((v) => !!v)
  }, [chFormattedProgressNotes, country, deFormattedProgressNotes, id])

  return (
    <Card>
      <CardHeader className="gap-4 pl-4">
        <div className="flex items-center gap-4">
          <UserProvider
            userName={updatedBy}
            render={(user) => <UserAvatarTooltip user={user} uniqueIdentifier={id} size="md" />}
          />
          <div>
            <div className="text-on-surface grid items-center gap-1 text-base font-medium">
              <div>
                {t(`scheduling.waitingRoom.service.${service}`)} {t("episodeOfCare.progressNote.for")}
              </div>
              <div className="-mt-1">
                {episodeOfCare
                  ? episodeOfCare.icdDiagnosis.text
                  : t("episodeOfCare.progressNote.unassignedEpisodeOfCare")}
              </div>
            </div>
            <div className="text-on-surface-subdued text-xs leading-4 font-normal tracking-[0.25px]">
              {formatDateString(createdAt)}
            </div>
          </div>
        </div>
      </CardHeader>
      {hasDocuments && (
        <CardBody className="border-t px-4 pb-4">
          <DocumentList
            isPreviewConclude={true}
            progressNote={progressNote}
            isEditable={false}
            isConclusion
            showTreatmentPlan={!!treatmentPlan}
            onSelect={onSelect}
          />
        </CardBody>
      )}
    </Card>
  )
}

export { ProgressNoteCard }
